import { FC, FormEvent } from "react";
import Modal from "components/Modal";
import { formatCountdown } from "shared/utils";
import type { AuthResponse } from "entities/user";
import { useTelegramVerify } from "../model/useTelegramVerify";

import styles from "./TelegramVerifyModal.module.css";

interface Props {
  challengeId: string;
  expiresAt: string;
  message?: string;
  onSuccess: (result: AuthResponse) => void;
  onClose: () => void;
}

export const TelegramVerifyModal: FC<Props> = ({
  challengeId,
  expiresAt,
  message,
  onSuccess,
  onClose,
}) => {
  const { code, setCode, submit, isSubmitting, errorMessage, isExpired, secondsLeft } =
    useTelegramVerify(challengeId, expiresAt, onSuccess);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  return (
    <Modal isOpen onClose={onClose} title="Confirm with Telegram">
      {isExpired ? (
        <div className={styles.content}>
          <p className={styles.status}>
            This code has expired. Please log in again.
          </p>
          <button type="button" className={styles.submitButton} onClick={onClose}>
            Close
          </button>
        </div>
      ) : (
        <form className={styles.content} onSubmit={handleSubmit}>
          <p className={styles.hint}>
            {message ?? "Enter the code sent to your Telegram."}
          </p>
          <input
            className={styles.codeInput}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="123456"
            inputMode="numeric"
            autoFocus
          />
          <p className={styles.timer}>Expires in {formatCountdown(secondsLeft)}</p>
          {errorMessage && <p className={styles.error}>{errorMessage}</p>}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting || !code.trim()}
          >
            {isSubmitting ? "Verifying..." : "Verify"}
          </button>
        </form>
      )}
    </Modal>
  );
};
